import { lighten } from "@mui/system";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default async function generatePdfTable({
  urlToBase64Url = "/interno/relatorio/urlToImageBase64",
  configUrl = "/interno/find/generalSetting",
  setLoadingPdf = () => {},
  rows = [[]],
  headers = [],
  theme = {},
  api = null,
  overrideParams = {},
  title = "relatorio.pdf",
}) {
  try {
    setLoadingPdf(true);

    // API segura com fallback
    const safeApi = api || {
      post: async () => ({ data: null }),
    };

    // Obter configurações de cabeçalho
    let config = {};
    let generalSettings = {};

    try {
      config = await safeApi.post(configUrl, {
        product: "usageReport",
      });

      const logo = config?.data?.imagem
        ? await handleUrlToBase64({
            url: config?.data?.imagem,
            api: safeApi, // Usar a API segura
            urlToBase64Url,
          })
        : null;

      generalSettings =
        config?.data && Object.keys(config.data).length > 0
          ? {
              logo: logo,
              contact: {
                whatsApp: config.data.whatsapp || null,
                mail: config.data.email || null,
                insta: config.data.instagram || null,
              },
            }
          : {};
    } catch (error) {
      console.error("Error fetching config: ", error);
      // Continua sem configurações personalizadas
      config = { data: null };
      generalSettings = {};
    }

    const doc = new jsPDF();
    const format = getImageFormatFromBase64(generalSettings?.logo);
    const margin = {
      top: 17,
      bottom: 12,
      left: 5,
      right: 5,
    };

    autoTable(doc, {
      margin,
      head: headers,
      body: rows,
      styles: {
        fontSize: 10,
      },
      theme: "grid",
      ...(!!theme?.palette && {
        headStyles: {
          fillColor: theme?.palette?.secondary?.dark,
        },
        alternateRowStyles: {
          fillColor: theme?.palette?.ligth?.secondary?.lighter,
        },
      }),
      columnStyles: (() => {
        // largura da página
        const pageWidth = doc.internal.pageSize.getWidth();
        // espaço útil depois das margens
        const usable = pageWidth - margin.left - margin.right; // left + right
        return {
          0: { cellWidth: usable * 0.15 },
          1: { cellWidth: usable * 0.2 },
          2: { cellWidth: usable * 0.15 },
          3: { cellWidth: usable * 0.38 },
          4: { cellWidth: usable * 0.12 },
        };
      })(),
      willDrawPage: (data) => {
        // Só adiciona a imagem se existir
        if (generalSettings?.logo) {
          doc.addImage(
            generalSettings.logo,
            format,
            data.settings.margin.left,
            data.settings.margin.top - 18,
            36,
            0
          );
        }
      },
      didDrawPage: (data) => {
        const pageHeight = doc.internal.pageSize.height;
        const marginRight =
          doc.internal.pageSize.width - data.settings.margin.right;
        const marginLeft = data.settings.margin.left;

        doc.setDrawColor(0, 0, 0); // Cor da linha (preto)
        doc.setLineWidth(0.1); // Largura da linha
        doc.line(marginLeft, pageHeight - 9, marginRight, pageHeight - 9); // Linha horizontal
        doc.setFontSize(8);

        // Exibe o número da página atual no rodapé
        doc.text(`${data.pageNumber}`, marginRight - 3, pageHeight - 5);

        // Obter nome da empresa de forma segura
        const appName =
          window?.config?.REACT_APP_NAME_ENTERPRISE ||
          process.env.REACT_APP_NAME_ENTERPRISE ||
          "do sistema";

        doc.text(
          `Dados obtidos pelo monitoramento ${appName}. Emitido em ${new Date().toLocaleString()}`,
          marginLeft + 1,
          pageHeight - 5
        );
      },
      ...overrideParams,
    });

    doc.save(title);
  } catch (error) {
    console.error("Erro ao gerar PDF", error);
    throw error;
  } finally {
    setLoadingPdf(false);
  }
}

async function handleUrlToBase64({
  url,
  options = {},
  api = null,
  urlToBase64Url,
}) {
  if (!api || typeof api.post !== "function") {
    console.warn("API não disponível para converter URL");
    return null;
  }

  try {
    const response = await api.post(urlToBase64Url, {
      url,
      options,
    });

    return response?.data || null;
  } catch (error) {
    console.error("Error converting URL to base64: ", error);
    return null;
  }
}

function getImageFormatFromBase64(base64) {
  if (!base64) return "PNG";

  const match = base64.match(/^data:image\/(.*?);base64,/);
  if (!match || !match[1]) return "PNG";

  let format = match[1].toLowerCase();

  // jsPDF usa 'JPEG' e 'PNG'
  if (format.includes("jpeg") || format.includes("jpg")) return "JPEG";
  if (format.includes("png")) return "PNG";

  // fallback
  return "PNG";
}
